import Loading from "../components/Loading";
import { getReportTemplateById, downloadReport, ReportFilter, ReportFilterOption, ItReportTemplateResponse } from "../services/reportService";
import { LanguageResponse } from "../services/languageService";
import { getLocations, LocationResponse } from "../services/locationService";
import { Box, Inline, Stack, TextField, Text, Button, Actions, Dropdown, IconPrint, IconDownload, Toggle, Autosuggest, Checkbox } from "braid-design-system"
import React from "react";
import { useParams } from "react-router-dom";
import { useToast } from "../hooks/useToast";
import { usePageHeading } from "../contexts/PageHeadingProvider";
import { config } from "../config/app.config";
import DateField from "../components/DateField";
import { useReportProgress } from "../hooks/useReportProgress";
import ReportProgressDisplay from "../components/Report/ReportProgressDisplay";

type ReportFilterResponse = ReportFilter;

// Memoized parameter field component to prevent re-renders on parent state changes
const ParameterField = React.memo(({ param, value, onChange, selectedLanguageCode, locations, parameterValues }: {
    param: ReportFilterResponse;
    value: any;
    onChange: (name: string, newValue: any, label?: string) => void;
    selectedLanguageCode: string;
    locations: LocationResponse[];
    parameterValues: { [key: string]: any };
}) => {
    const label = (param.label || param.name) + (param.is_required ? " *" : "");
    const [inputValue, setInputValue] = React.useState<string>("");
    const todayValue = React.useMemo(() => new Date().toISOString().slice(0, 10), []);

    switch (param.type) {
        case "string":
            return <TextField
                key={param.name}
                value={value || ""}
                placeholder={param.label || ""}
                label={label}
                onChange={(e) => onChange(param.name, e.currentTarget.value)}
            />;
        case "integer":
            return <TextField
                key={param.name}
                type="number"
                value={value || ""}
                placeholder={param.label || ""}
                label={label}
                onChange={(e) => onChange(param.name, e.currentTarget.value)}
            />;
        case "date":
            return <DateField
                key={param.name}
                value={value || ""}
                label={label}
                onChange={(dateValue) => onChange(param.name, dateValue)}
            />;
        case "today_date":
            return <TextField
                key={param.name}
                type="text"
                value={value || ""}
                label={label}
                placeholder={param.label || ""}
                onChange={(e) => onChange(param.name, e.currentTarget.value !== '' ? todayValue : '')}
            />;
        case "boolean":
            return <Toggle on={Boolean(value)} onChange={(checked) => onChange(param.name, checked)} label={param.label || param.name} />;
        case "select":
            const selectOptions = [
                { value: config.allValue, label: "All" },
                ...(param.options || [])
            ];

            // Create display options which prefer language-specific labels when available,
            // otherwise fall back to the default label
            const displayOptions = selectOptions.map(opt => {
                // option may contain language-specific properties like `label_en`, `label_fr`, etc.
                const langKey = `label_${selectedLanguageCode}`;
                const langLabel = (opt as any)[langKey];
                const displayLabel = langLabel && String(langLabel).trim() !== '' ? String(langLabel) : String(opt.label || opt.value);
                return {
                    value: opt.value,
                    label: opt.label,
                    displayLabel
                };
            });

            // Use Autosuggest for select parameters with many options, Dropdown for few options
            const useAutosuggest = selectOptions.length > 10;

            if (useAutosuggest) {
                const selectedOption = value
                    ? displayOptions.find(o => String(o.value) === String(value))
                    : null;

                const filteredSuggestions = inputValue.trim() === ''
                    ? displayOptions.map(opt => ({
                        text: opt.displayLabel,
                        value: String(opt.value)
                    }))
                    : displayOptions
                        .filter(opt => (opt.label || '').toString().toLowerCase().includes(inputValue.toLowerCase()) || (opt.displayLabel || '').toString().toLowerCase().includes(inputValue.toLowerCase()))
                        .map(opt => ({
                            text: opt.displayLabel,
                            value: String(opt.value)
                        }));

                return <Autosuggest
                    key={param.name}
                    label={label}
                    placeholder={param.label || "Search..."}
                    suggestions={filteredSuggestions}
                    value={{
                        text: inputValue || (selectedOption ? selectedOption.displayLabel : ''),
                        value: value ? String(value) : ''
                    }}
                    onChange={(suggestion: any) => {
                        // When user selects a suggestion
                        if (suggestion?.value) {
                            const selectedOpt = selectOptions.find(o => String(o.value) === String(suggestion.value));
                            onChange(param.name, suggestion.value, selectedOpt?.label);
                            setInputValue('');
                        } else {
                            // When user types in the field
                            setInputValue(suggestion?.text || '');
                        }
                    }}
                    onClear={() => {
                        setInputValue('');
                        onChange(param.name, '', '');
                    }}
                />;
            } else {
                return <Dropdown
                    key={param.name}
                    label={label}
                    value={value || config.allValue}
                    placeholder={param.label || ""}
                    onChange={(event) => {
                        const selectedValue = (event.target as HTMLSelectElement).value;
                        const selectedOption = selectOptions.find(opt => String(opt.value) === String(selectedValue));
                        const selectedLabel = selectedOption?.label || selectedValue;
                        onChange(param.name, selectedValue, selectedLabel);
                    }}
                >
                    {displayOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.displayLabel}
                        </option>
                    ))}
                </Dropdown>;
            }
        case "location_multiselect":
            // Use locations from param.options if provided, otherwise use fetched locations
            const locationOptions = param.options || locations.map(loc => ({
                value: loc.loc_code || '',
                label: loc.loc_desc || loc.loc_code || ''
            }));
            
            // Get current selected locations (value can be array or comma-separated string)
            const selectedLocations = Array.isArray(value) ? value : (value ? String(value).split(',') : []);
            
            const handleLocationToggle = (locCode: string) => {
                const current = Array.isArray(parameterValues[param.name]) 
                    ? parameterValues[param.name] as string[]
                    : (parameterValues[param.name] ? String(parameterValues[param.name]).split(',') : []);
                
                let updated: string[];
                if (current.includes(locCode)) {
                    updated = current.filter(loc => loc !== locCode);
                } else {
                    updated = [...current, locCode];
                }
                
                // Create label showing selected locations
                const selectedLabels = updated.map(code => {
                    const loc = locationOptions.find(l => l.value === code);
                    return loc ? loc.label : code;
                });
                const labelText = updated.length === 0 
                    ? "All Locations" 
                    : updated.length === locationOptions.length 
                        ? "All Locations" 
                        : selectedLabels.join(', ');
                
                onChange(param.name, updated, labelText);
            };

            const handleSelectAll = (checked: boolean) => {
                if (checked) {
                    // Select all locations
                    const allLocations = locationOptions.map(loc => String(loc.value));
                    onChange(param.name, allLocations, "All Locations");
                } else {
                    // Deselect all locations
                    onChange(param.name, [], "All Locations");
                }
            };

            const isAllSelected = selectedLocations.length === 0 || selectedLocations.length === locationOptions.length;

            return (
                <Box key={param.name} paddingBottom="small">
                    <Text weight="strong" tone="neutral">{label}</Text>
                    <Box 
                        background="neutralLight" 
                        borderRadius="small" 
                        padding="small"
                        style={{ maxHeight: '200px', overflowY: 'auto' }}
                    >
                        <Stack space="xsmall">
                            <Inline space="small" alignY="center">
                                <Checkbox
                                    label="All"
                                    checked={isAllSelected}
                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                />
                            </Inline>
                            {locationOptions.map((loc: ReportFilterOption) => {
                                const locCode = String(loc.value);
                                const isChecked = selectedLocations.includes(locCode);
                                return (
                                    <Inline key={locCode} space="small" alignY="center">
                                        <Checkbox
                                            label={loc.label}
                                            checked={isChecked}
                                            onChange={() => handleLocationToggle(locCode)}
                                        />
                                    </Inline>
                                );
                            })}
                        </Stack>
                    </Box>
                </Box>
            );
        default:
            return null;
    }
});

ParameterField.displayName = 'ParameterField';

const Report: React.FC = () => {
    const [templateResponse, setTemplateResponse] = React.useState<ItReportTemplateResponse | null>(null);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [downloading, setDownloading] = React.useState<boolean>(false);
    const [reportHtml, setReportHtml] = React.useState<string | null>(null);
    const [generatedReportId, setGeneratedReportId] = React.useState<number | null>(null);
    const { reportTemplateId } = useParams();
    const toast = useToast();
    const [parameterValues, setParameterValues] = React.useState<{ [key: string]: any }>({});
    const [parameterLabels, setParameterLabels] = React.useState<{ [key: string]: string }>({});  // Track selected option labels
    const { setPageHeading } = usePageHeading();
    const [languages, setLanguages] = React.useState<LanguageResponse[]>([]);
    const [selectedLanguageCode, setSelectedLanguageCode] = React.useState<string>('en'); // Default to English
    const [reportZoom, setReportZoom] = React.useState<number>(100); // Zoom level in percentage (100% = normal)
    const [isPrinting, setIsPrinting] = React.useState<boolean>(false);
    const [locations, setLocations] = React.useState<LocationResponse[]>([]);

    // Progress tracking via WebSocket
    const { progressState, connectWebSocket, reset: resetProgress } = useReportProgress(generatedReportId);

    const handleParameterChange = React.useCallback((name: string, newValue: any, label?: string) => {
        setParameterValues((prev) => ({
            ...prev,
            [name]: newValue
        }));
        // Store the selected option label if provided
        if (label) {
            setParameterLabels((prev) => ({
                ...prev,
                [name]: label
            }));
        }
    }, []);

    const renderParamField = (param: ReportFilterResponse) => {
        return <ParameterField
            key={param.name}
            param={param}
            value={parameterValues[param.name]}
            onChange={handleParameterChange}
            selectedLanguageCode={selectedLanguageCode}
            locations={locations}
            parameterValues={parameterValues}
        />;
    };

    // Fetch locations on mount
    React.useEffect(() => {
        const fetchLocations = async () => {
            try {
                const locs = await getLocations();
                setLocations(locs);
            } catch (error) {
                // Silently handle error - locations will not be shown
            }
        };
        fetchLocations();
    }, []);

    React.useEffect(() => {
        setPageHeading("Report Viewer");
        if (!reportTemplateId) {
            return
        }
        if (reportTemplateId) {
            setLoading(true);
            getReportTemplateById(Number(reportTemplateId)).then((templateResponse) => {
                setTemplateResponse(templateResponse);
                const reportLanguages = Array.isArray(templateResponse.languages) ? templateResponse.languages : [];
                setLanguages(reportLanguages);

                const availableLanguageCodes = reportLanguages
                    .map((language) => language.code)
                    .filter((code): code is string => Boolean(code));
                if (availableLanguageCodes.length > 0) {
                    setSelectedLanguageCode((current) => (
                        availableLanguageCodes.includes(current) ? current : availableLanguageCodes[0]
                    ));
                }
                setPageHeading(templateResponse.name ?? "Report Viewer");

                // Initialize parameter labels for all dropdown parameters with their default values
                const initialLabels: { [key: string]: string } = {};
                if (templateResponse.parameters) {
                    for (const param of templateResponse.parameters) {
                        if (param.type === 'select' && param.options) {
                            // Always initialize with "All" label since default is config.allValue
                            initialLabels[param.name] = "All";
                        }
                    }
                }
                setParameterLabels(initialLabels);
            }).catch((error) => {
                toast.showError(`Error fetching report template: ${error.message}`);
            }).finally(() => {
                setLoading(false);
            });
        }

    }, [reportTemplateId, setPageHeading, toast]);

    // Calculate file size in KB
    const getFileSizeInKB = (html: string): number => {
        return new Blob([html]).size / 1024;
    };

    // Handle smart printing - download PDF for large files, print HTML for small files
    const handleSmartPrint = async () => {
        if (!reportHtml) return;

        try {
            setIsPrinting(true);
            const fileSizeKB = getFileSizeInKB(reportHtml);

            // If file is larger than 5MB, recommend PDF download instead
            if (fileSizeKB > 5120) {
                toast.showWarning(
                    "Large File",
                    `Report is ${(fileSizeKB / 1024).toFixed(2)}MB. Printing may be slow...`
                );
                // Still attempt to print but with chunked approach
                handleChunkedPrint();
            } else if (fileSizeKB > 1024) {
                // For files 1-5MB, use chunked printing
                toast.showInfo("Preparing", "Preparing large report for printing...");
                handleChunkedPrint();
            } else {
                // For small files, use normal print
                handleNormalPrint();
            }
        } finally {
            setIsPrinting(false);
        }
    };

    // Normal print for small files
    const handleNormalPrint = () => {
        if (!reportHtml) return;

        const printWindow = window.open('', '_blank');
        if (printWindow) {
            const printStyles = `<style>
                * {
                    margin: 0 !important;
                    padding: 0 !important;
                }
                @page {
                    margin: 0 !important;
                    padding: 0 !important;
                }
                html, body {
                    margin: 0 !important;
                    padding: 0 !important;
                    width: 100% !important;
                }
                table {
                    page-break-inside: avoid;
                }
                tr {
                    page-break-inside: avoid;
                }
            </style>`;

            let htmlWithStyles = reportHtml;
            if (reportHtml.includes('</head>')) {
                htmlWithStyles = reportHtml.replace('</head>', printStyles + '</head>');
            } else {
                htmlWithStyles = printStyles + reportHtml;
            }

            printWindow.document.write(htmlWithStyles);
            printWindow.document.close();

            setTimeout(() => {
                printWindow.print();
            }, 500);
        }
    };

    // Chunked print for large files - renders in parts to avoid crash
    const handleChunkedPrint = () => {
        if (!reportHtml) return;

        const printWindow = window.open('', '_blank');
        if (!printWindow) return;

        const printStyles = `<style>
            * {
                margin: 0 !important;
                padding: 0 !important;
            }
            @page {
                margin: 0 !important;
                padding: 0 !important;
            }
            html, body {
                margin: 0 !important;
                padding: 0 !important;
                width: 100% !important;
            }
            table {
                page-break-inside: avoid;
            }
            tr {
                page-break-inside: avoid;
            }
        </style>`;

        // Start document
        printWindow.document.write('<!DOCTYPE html><html><head>' + printStyles + '</head><body>');

        // Split HTML into chunks and write incrementally
        const chunkSize = 50000; // Characters per chunk
        for (let i = 0; i < reportHtml.length; i += chunkSize) {
            const chunk = reportHtml.substring(i, i + chunkSize);
            printWindow.document.write(chunk);
        }

        // Close document and print
        printWindow.document.write('</body></html>');
        printWindow.document.close();

        setTimeout(() => {
            printWindow.print();
        }, 1000);
    };

    const handleGenerateReport = async () => {
        if (!reportTemplateId) {
            toast.showError("Report template ID is missing");
            return;
        }

        // Validate required parameters
        if (templateResponse?.parameters) {
            const missingRequiredParams: string[] = [];

            for (const param of templateResponse.parameters) {
                // Check if is_required field exists
                if (param.is_required === undefined || param.is_required === null) {
                    toast.showError(`Parameter "${param.name}" is missing the "is_required" field configuration`);
                    return;
                }

                // Check if required parameter has a value
                if (param.is_required && (!parameterValues[param.name] || parameterValues[param.name] === config.allValue)) {
                    missingRequiredParams.push(param.label || param.name);
                }
            }

            // Show error if any required parameters are missing
            if (missingRequiredParams.length > 0) {
                toast.showError(`Please fill in required parameters: ${missingRequiredParams.join(", ")}`);
                return;
            }
        }

        try {
            setDownloading(true);
            setReportHtml(null);

            // Set a report ID for progress tracking (using reportTemplateId as base)
            const progressReportId = Number(reportTemplateId);
            setGeneratedReportId(progressReportId);
            resetProgress();

            // Connect to WebSocket for progress updates
            setTimeout(() => connectWebSocket(), 100);

            // Prepare parameter labels for backend: prefer language-specific option label when available,
            // otherwise fall back to the base label stored in parameterLabels
            const parameterLabelsForBackend: { [key: string]: string } = {};
            if (templateResponse?.parameters) {
                for (const p of templateResponse.parameters) {
                    const name = p.name;
                    const baseLabel = parameterLabels[name] || '';

                    let finalLabel = baseLabel;

                    // Only try to resolve option-specific labels for select params
                    if (p.type === 'select' && p.options && parameterValues[name] !== undefined && parameterValues[name] !== null) {
                        const selectedVal = parameterValues[name];
                        const matchedOpt = p.options.find((o: ReportFilterOption) => String(o.value) === String(selectedVal));
                        if (matchedOpt) {
                            const langKey = `label_${selectedLanguageCode}`;
                            const langLabel = (matchedOpt as any)[langKey];
                            if (langLabel && String(langLabel).trim() !== '') {
                                finalLabel = String(langLabel);
                            }
                        }
                    }

                    parameterLabelsForBackend[name] = finalLabel;
                }
            } else {
                // Fallback: send whatever labels we have (unchanged)
                Object.keys(parameterLabels).forEach((k) => {
                    parameterLabelsForBackend[k] = parameterLabels[k] || '';
                });
            }

            const html = await downloadReport(Number(reportTemplateId), parameterValues, 'html', selectedLanguageCode, parameterLabelsForBackend);
            if (html) {
                setReportHtml(html);
                toast.showSuccess("Report generated successfully!");

                // Show progress completion for 1.5 seconds before hiding the progress display
                await new Promise(resolve => setTimeout(resolve, 1500));
            }
        } catch (error: any) {
            //eslint-disable-next-line no-console
            console.error("Error generating report:", error);
            toast.showError(`Error: ${error.message}`);
        } finally {
            setDownloading(false);
        }
    };

    // Memoize the report HTML rendering to prevent re-renders when parameters change
    const renderedReportHtml = React.useMemo(() => {
        if (!reportHtml) return null;
        // Wrap in fm-abhay font class
        return <div className="fm-abhay" dangerouslySetInnerHTML={{ __html: reportHtml }} />;
    }, [reportHtml]);

    return (
        <>
            {loading && !downloading ? (
                <Loading message="Please wait, report template is loading..." />
            ) : null}

            <Box padding="small">
                <Stack space="small">
                    <Box display={"flex"} justifyContent={"flexEnd"}>
                        <Inline space={"small"} alignY="center">
                            {languages.length > 0 && (
                                <Dropdown
                                    label="Language"
                                    value={selectedLanguageCode}
                                    onChange={(event) => {
                                        const languageCode = (event.target as HTMLSelectElement).value;
                                        setSelectedLanguageCode(languageCode);
                                    }}
                                >
                                    {languages
                                        .filter((language) => Boolean(language.code))
                                        .map((language) => (
                                            <option key={language.id} value={language.code!}>
                                                {language.display_name}
                                            </option>
                                        ))}
                                </Dropdown>
                            )}
                            {templateResponse?.parameters?.map((param: ReportFilterResponse) =>
                                renderParamField(param)
                            )}
                        </Inline>
                    </Box>
                    <Box display={"flex"} justifyContent={"flexEnd"}>
                        <Actions>
                            <Button icon={<IconDownload />} size="small" variant="ghost" onClick={() => {
                                if (!downloading) {
                                    handleGenerateReport();
                                }
                            }
                            }>
                                {downloading ? "Generating..." : "Generate Report"}
                            </Button>
                        </Actions>
                    </Box>

                    {downloading && (
                        <Box padding="small" background="neutralLight" borderRadius="small">
                            <ReportProgressDisplay progressState={progressState} />
                        </Box>
                    )}
                </Stack>
            </Box>
            {reportHtml &&
                <Box padding="small" background="neutralLight">
                    <Stack space="small">
                        <Box display="flex" justifyContent="spaceBetween" alignItems="center">
                            <Text weight="strong" tone="info">Report Preview:</Text>
                            <Inline space="small" alignY="center">
                                <Button
                                    size="small"
                                    variant="ghost"
                                    onClick={() => setReportZoom(Math.max(50, reportZoom - 10))}
                                >
                                    −
                                </Button>
                                <Box style={{ minWidth: '60px', textAlign: 'center' }}>
                                    <Text size="small" tone="secondary">
                                        {reportZoom}%
                                    </Text>
                                </Box>
                                <Button
                                    size="small"
                                    variant="ghost"
                                    onClick={() => setReportZoom(Math.min(200, reportZoom + 10))}
                                >
                                    +
                                </Button>
                                <Button
                                    size="small"
                                    variant="ghost"
                                    onClick={() => setReportZoom(100)}
                                >
                                    Reset
                                </Button>
                                <Button tone="brandAccent" icon={<IconPrint />} size="small" variant="ghost" onClick={handleSmartPrint}>
                                    {isPrinting ? "Preparing..." : "Print Report"}
                                </Button>
                            </Inline>
                        </Box>
                        <Box
                            display={"block"}
                            style={{
                                transform: `scale(${reportZoom / 100})`,
                                transformOrigin: 'top left',
                                width: `${100 / (reportZoom / 100)}%`,
                                height: 'auto'
                            }}
                        >
                            {renderedReportHtml}
                        </Box>
                    </Stack>
                </Box>
            }
        </>
    )
}

export default Report